import React, { useState } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";

const tipoOperacionLabels = {
    1: { icono: "💰", texto: "Venta" },
    2: { icono: "🏠", texto: "Alquiler" },
    3: { icono: "🏖️", texto: "Vacacional" }
};

const PropertyCard = ({
                          prop,
                          navigate,
                          calcularReforma,
                          calcularIndicadores,
                          calculando,
                          esFavorita,
                          toggleStarred,
                          isLoggedIn
                      }) => {
    const [indicadores, setIndicadores] = useState(prop.indicadores || []);

    const tieneReforma =
        prop.reforma_estandar_min !== null || prop.reforma_premium_min !== null;

    const handleStarClick = (e) => {
        e.stopPropagation();
        toggleStarred(prop.id, esFavorita);
    };

    const calcularYMostrarIndicadores = async (e) => {
        e.stopPropagation();
        try {
            const res = await fetch(
                `${window.REACT_APP_API_ENDPOINT}/indicadores/calcular-indicadores/${prop.id}`,
                {
                    method: "POST",
                    credentials: "include"
                }
            );
            const data = await res.json();
            setIndicadores(data);
        } catch (err) {
            console.error("Error al calcular indicadores:", err);
        }
    };

    const agruparIndicadoresPorOperacion = () => {
        const agrupados = {
            1: [], // Venta
            2: [], // Alquiler
            3: []  // Vacacional
        };

        for (const indicador of indicadores) {
            if (agrupados[indicador.tipoOperacionId]) {
                agrupados[indicador.tipoOperacionId].push(indicador);
            }
        }

        return agrupados;
    };

    const mostrarRentabilidades = () => {
        if (!indicadores || indicadores.length === 0) return null;

        const agrupados = agruparIndicadoresPorOperacion();

        return (
            <div className="mt-2 space-y-1 p-2 bg-green-100 border-l-4 border-green-500 text-green-700 font-semibold text-sm">
                {Object.entries(agrupados).map(([tipoId, lista]) => {
                    const label = tipoOperacionLabels[tipoId];
                    if (!label || lista.length === 0) return null;

                    const rentabilidades = lista
                        .map(i => parseFloat(i.rentabilidadAnual))
                        .filter(v => !isNaN(v));

                    if (rentabilidades.length === 0) return null;

                    const min = Math.min(...rentabilidades);
                    const max = Math.max(...rentabilidades);

                    return (
                        <p key={tipoId}>
                            <span className="inline-block bg-green-200 text-green-800 text-xs px-2 py-1 rounded-full mr-2">
                                {label.icono} {label.texto}
                            </span>
                            Rentabilidad estimada: {min.toFixed(2)}% – {max.toFixed(2)}%
                        </p>
                    );
                })}
            </div>
        );
    };

    const mostrarBotonesReforma = !tieneReforma;
    const mostrarBotonesIndicadores = indicadores.length === 0;

    const mostrarIconoIA = () => {
        if (prop.numImagenes >= 4) {
            if (prop.yaAnalizada) {
                return (
                    <div
                        className="absolute top-2 left-2 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold shadow"
                        title="Esta propiedad ya fue analizada por IA"
                    >
                        🤖 Analizada IA
                    </div>
                );
            } else {
                return (
                    <div
                        className="absolute top-2 left-2 bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-semibold shadow"
                        title="Esta propiedad puede ser analizada por IA"
                    >
                        ⚡ Análisis IA disponible
                    </div>
                );
            }
        }
        return null;
    };

    return (
        <div
            className={`bg-white shadow-lg rounded-lg p-4 border relative ${
                tieneReforma ? "cursor-pointer hover:shadow-xl transition-shadow duration-300" : ""
            }`}
            onClick={() => tieneReforma && navigate(`/propiedad/${prop.id}`)}
        >
            {isLoggedIn && (
                <div
                    className="absolute top-2 right-2 text-yellow-500 text-xl cursor-pointer z-10"
                    onClick={handleStarClick}
                    title={esFavorita ? "Desmarcar como favorita" : "Marcar como favorita"}
                >
                    {esFavorita ? <FaStar /> : <FaRegStar />}
                </div>
            )}

            {mostrarIconoIA()}

            {/* Imagen */}
            {prop.imagen_url && (
                <img
                    src={prop.imagen_url}
                    alt="Propiedad"
                    className="rounded-lg w-full h-48 object-cover mb-3"
                />
            )}

            <h2 className="text-lg font-semibold">
                {prop.habitaciones > 0 ? `${prop.habitaciones} hab.` : "Estudio"} – {prop.banos} baño{prop.banos > 1 ? "s" : ""}, {prop.superficie} m²
            </h2>
            <p className="text-sm text-gray-500">
                {prop.distrito ? `${prop.distrito}, ${prop.municipio}` : prop.municipio}
            </p>
            <p className="text-lg font-bold text-blue-600">
                {parseFloat(prop.precio).toLocaleString(undefined, { minimumFractionDigits: 2 })} €
            </p>

            {tieneReforma && (
                <div className="mt-4 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 font-semibold">
                    {prop.reforma_estandar_min !== null && (
                        <p>
                            Reforma Estándar: {parseFloat(prop.reforma_estandar_min).toLocaleString()} € - {parseFloat(prop.reforma_estandar_max).toLocaleString()} €
                        </p>
                    )}
                    {prop.reforma_premium_min !== null && (
                        <p>
                            Reforma Premium: {parseFloat(prop.reforma_premium_min).toLocaleString()} € - {parseFloat(prop.reforma_premium_max).toLocaleString()} €
                        </p>
                    )}
                </div>
            )}

            {(mostrarBotonesReforma || mostrarBotonesIndicadores) && (
                <div className="mt-4 text-center space-y-2">
                    {mostrarBotonesReforma && (
                        <button
                            className="bg-blue-600 hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded w-full"
                            onClick={(e) => {
                                e.stopPropagation();
                                calcularReforma(prop.id);
                            }}
                            disabled={calculando}
                        >
                            {calculando ? "Calculando..." : "Calcular coste de reforma"}
                        </button>
                    )}

                    {mostrarBotonesIndicadores && (
                        <button
                            className="bg-green-600 hover:bg-green-800 text-white font-semibold py-2 px-4 rounded w-full"
                            onClick={calcularYMostrarIndicadores}
                            disabled={calculando}
                        >
                            {calculando ? "Calculando..." : "Calcular rentabilidad"}
                        </button>
                    )}
                </div>
            )}

            {mostrarRentabilidades()}
        </div>
    );
};

export default PropertyCard;
